import { Redirect, Route, Switch } from 'react-router-dom';
import { stringHelper } from '@zg-rentals/rental-manager-env';
import { parseQueryString } from '@zg-rentals/string-utils';
import { findKey } from '@zg-rentals/zdash';

import { logInfo } from './loggers/appLogger';

import legacyRouteRedirects from './utils/legacyRouteRedirects';
import StringConstants from './constants/StringConstants';
import QueryConstants from './constants/QueryConstants';
import { ERROR_PAGE_TYPES } from './constants/ErrorMessageConstants';
import pathsManifest, { getSubPathList, RMW_PATH, RMW_SUB_PATH } from './pathsManifest';

// page containers
import DashboardPage from './containers/DashboardPage';
import LeadPage from './containers/LeadPage/Loadable';
import ListingLandingPage from './containers/ListingLandingPage';
import ErrorPage from './containers/ErrorPage';
import SecurityInquiryPage from './containers/SecurityInquiryPage';
import SelectPropertyPage from './containers/SelectPropertyPage/Loadable';
import MobilePaymentPage from './containers/MobilePaymentPage';
import InquiryContactPage from './containers/InquiryContactPage';
import RentGuaranteePage from './containers/RentGuaranteePage/Loadable';
import CreateListingPage from './containers/CreateListingPage/Loadable';
import SubmitRequestPage from './containers/SubmitRequestPage';
import RentGuaranteeManagePoliciesPage from './containers/RentGuaranteeManagePoliciesPage/Loadable';
import PremiumAnalyticsPage from './containers/PremiumAnalyticsPage/Loadable';
import PropertiesPageWrapper from './containers/PropertiesPageWrapper';
import ListingAvailabilityConfirmationPage from './containers/ListingAvailabilityConfirmationPage/Loadable';
import RentGuaranteeCoverageLevelSelectionPage from './containers/RentGuaranteeCoverageLevelSelectionPage';
import RentalProtectionPremiumPaymentsPage from './containers/RentalProtectionPremiumPaymentsPage/Loadable';
import TourAvailabilityPage from './containers/TourAvailabilityPage/Loadable';
import TourFullCalendarPage from './containers/TourFullCalendarPage/Loadable';

/**
 * a list of paths that will enable a full screen wizard view
 *  via dispatch(updateIsWizardView(true))
 *
 * we need to set this server side in order to create a seamless loading experience
 */
const FULL_SCREEN_WIZARD_VIEW_PATHS = {
  // NOTE: PDP (MF) is not wizardView eventho it is `/listing`
  LISTING_PATH: '/:propertyId/listing',
  CREATE_LISTING: '/:propertyId/create-listing',
};

// conditional import to skip loadable
let PropertyDetailsPage = null;
function importComponents(component) {
  if (component === 'propertyDetailsPage' && __SERVER__) {
    PropertyDetailsPage = require('./containers/PropertyDetailsPage/');
  } else {
    PropertyDetailsPage = require('./containers/PropertyDetailsPage/').default;
  }
}

export default function Routes({ brand, isRunningInProd, pathname, isInReactivationFlow } = {}) {
  const paths = pathsManifest.getAllPaths(brand);
  const allowParamsRegex = StringConstants.ALLOW_PARAMS_REGEX;

  // all of the paths that include /dashboard/{subPath}
  const allowDashboardParamsRegex = stringHelper.getAllowDashboardParamsRegexByPaths({
    pathList: getSubPathList({ pathObj: RMW_PATH, subPathBase: RMW_SUB_PATH.DASHBOARD }),
  });

  const zillowBrand = StringConstants.UNFORMATTED_BRAND_NAMES.zillow;
  const component = findKey(paths, (path) => path === pathname);

  importComponents(component);

  /*
   * NOTE: react route render props are undefined when hitting the route directly
   * renderProps.component is undefined server side in authenticate.js
   * bypassing getUser to set the user model
   *
   * Wrapping redirect component within a function fixes the issue.
   * https://github.com/ReactTraining/react-router/issues/4942
   */
  const redirectRouteList = {
    editListing: {
      path: `${paths.editListingPage}/:propertyId*${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/listing`} />}
        />
      ),
    },
    findTenants: {
      path: `${paths.propertiesPage}/:propertyId*/find-tenants${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => {
            const {
              match: {
                params: { propertyId },
              },
            } = props;

            logInfo({
              message: 'Redirected route from /find-tenants to /dashboard/overview',
              context: 'routes@findTenants.getRedirectRoute',
              listingId: propertyId,
            });

            return <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/dashboard/overview`} />;
          }}
        />
      ),
    },
    manageTenants: {
      path: `${paths.propertiesPage}/:propertyId*/manage-tenants${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => {
            const {
              match: {
                params: { propertyId },
              },
            } = props;

            logInfo({
              message: 'Redirected route from /manage-tenants to /dashboard/overview',
              context: 'routes@manageTenants.getRedirectRoute',
              listingId: propertyId,
            });

            return <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/dashboard/overview`} />;
          }}
        />
      ),
    },
    screeningCriteria: {
      path: `${paths.propertiesPage}/:propertyId/screening-criteria${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => (
            <Redirect
              to={`${paths.propertiesPage}/${props.match.params.propertyId}/listing?${QueryConstants.SCROLL_TO}=screeningCriteria`}
            />
          )}
        />
      ),
    },
    statistics: {
      path: `${paths.statisticsPage}/:propertyId*${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/metrics`} />}
        />
      ),
    },

    payments: {
      path: `${paths.propertiesPage}/:propertyId/payments${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => <Redirect to={`/rental-manager/payments/properties/${props.match.params.propertyId}`} />}
        />
      ),
    },
    leases: {
      path: `${paths.propertiesPage}/:propertyId/leases${allowParamsRegex}`,
      getRedirectRoute: () => {
        return (
          <Route
            render={(props) => <Redirect to={`/rental-manager/leasing/property/${props.match.params.propertyId}`} />}
          />
        );
      },
    },
    propertyDetailsStatistics: {
      path: `${paths.propertiesPage}/:propertyId/statistics${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/analytics`} />}
        />
      ),
    },
    propertyDetailsInbox: {
      path: `${paths.propertiesPage}/:propertyId/inbox${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/contacts`} />}
        />
      ),
    },
    leads: {
      path: `${paths.leadsPage}/:propertyId*${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/contacts`} />}
        />
      ),
    },
    propertyLeadsPage: {
      path: `${paths.propertiesPage}/:propertyId/leads${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          render={(props) => <Redirect to={`${paths.propertiesPage}/${props.match.params.propertyId}/contacts`} />}
        />
      ),
    },
    listingTeardownPage: {
      path: `${paths.listingTeardownPage}${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route
          exact={true}
          render={(props) => {
            const queryParams = parseQueryString(props.location.search);
            const encodedAlias = queryParams[QueryConstants.ENCODED_ALIAS];
            const redirectUrl = encodedAlias ? `${paths.propertiesPage}/${encodedAlias}/listing` : paths.propertiesPage;
            return <Redirect to={redirectUrl} />;
          }}
        />
      ),
    },
    tenantPlacement: {
      path: `${paths.tenantPlacement}${allowParamsRegex}`,
      getRedirectRoute: () => <Route render={(props) => <Redirect to={`${paths.base}`} />} />,
    },
    idVerification: {
      path: `${paths.idVerification}${allowParamsRegex}`,
      getRedirectRoute: () => (
        <Route render={(props) => <Redirect to={'/rental-manager/applications/id-verification'} />} />
      ),
    },
  };

  /*
   * NOTE: Adding regex ? to end of paths because v4 router does not handle this well.
   * Allows: ?, ?myparam, ?myparam=, ?myparam=hello, ?myparam=hello&something.
   * Disallows: /my-listingsPlusExtraChars
   * https://github.com/ReactTraining/react-router/issues/5381
   */
  return (
    <Switch>
      <Route
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/${RMW_SUB_PATH.DASHBOARD}/${allowDashboardParamsRegex}${allowParamsRegex}`}
        component={DashboardPage}
      />

      {/* redirect `/rental-manager/payments` to `/properties` */}
      <Route
        exact={true}
        path={`/rental-manager/payments${allowParamsRegex}`}
        render={() => <Redirect to={`${paths.propertiesPage}`} />}
      />
      {/* redirect `/rental-manager/leases` to `/propertie`s */}
      <Route
        exact={true}
        path={`/rental-manager/leases${allowParamsRegex}`}
        render={() => <Redirect to={`${paths.propertiesPage}`} />}
      />
      {/* redirect /my-listings to /properties */}
      <Route
        path={`${paths.myListingsPage}${allowParamsRegex}`}
        render={() => <Redirect to={`${paths.propertiesPage}`} />}
      />
      {/* security-inquiry */}
      <Route
        path={`${paths.securityInquiry}`}
        exact={true}
        render={(props) => {
          const listingAlias = props.location?.state?.listingAlias;
          return <SecurityInquiryPage key={listingAlias} />;
        }}
      />
      {/* select-property */}
      {/* redirect logic is in matchRoute.js  */}
      <Route component={SelectPropertyPage} exact={true} path={`/rental-manager/select-property${allowParamsRegex}`} />

      {/* properties */}
      <Route component={PropertiesPageWrapper} exact={true} path={`${paths.propertiesPage}${allowParamsRegex}`} />
      {/* property details - edit */}
      <Route
        component={isInReactivationFlow ? CreateListingPage : PropertyDetailsPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/listing${allowParamsRegex}`}
      />
      {/* property - create listing */}
      <Route
        component={CreateListingPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/create-listing${allowParamsRegex}`}
      />
      {/* property details - applications */}
      <Route
        pageWithSensitiveInformation={true}
        component={PropertyDetailsPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/applications${allowParamsRegex}`}
      />

      {/* redirect /rental-manager/properties/:propertyId/find-tenants to /rental-manager/properties/:propertyId/dashboard/overview */}
      <Route exact={true} path={redirectRouteList.findTenants.path}>
        {redirectRouteList.findTenants.getRedirectRoute()}
      </Route>

      {/* redirect /rental-manager/properties/:propertyId/manage-tenants to /rental-manager/properties/:propertyId/dashboard/overview */}
      <Route exact={true} path={redirectRouteList.manageTenants.path}>
        {redirectRouteList.manageTenants.getRedirectRoute()}
      </Route>

      {/* property details - leases */}
      {/* redirect /rental-manager/properties/:propertyId/leases to /rental-manager/leasing/property/:propertyId */}
      <Route exact={true} path={redirectRouteList.leases.path}>
        {redirectRouteList.leases.getRedirectRoute()}
      </Route>
      {/* property details - payments */}
      {/* redirect /rental-manager/properties/:propertyId/payments to /rental-manager/payments/properties/:propertyId */}
      <Route exact={true} path={redirectRouteList.payments.path}>
        {redirectRouteList.payments.getRedirectRoute()}
      </Route>
      {/* redirect /rental-manager/tenant-placement to /rental-manager/ */}
      <Route exact={true} path={redirectRouteList.tenantPlacement.path}>
        {redirectRouteList.tenantPlacement.getRedirectRoute()}
      </Route>
      {/* redirect /rental-manager/id-verification to /rental-manager/applications/id-verification */}
      <Route exact={true} path={redirectRouteList.idVerification.path}>
        {redirectRouteList.idVerification.getRedirectRoute()}
      </Route>

      {/* property details - contacts */}
      <Route
        component={PropertyDetailsPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/contacts${allowParamsRegex}`}
      />
      {/* Premium Analytics Page */}
      <Route
        component={PremiumAnalyticsPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/${RMW_PATH.PREMIUM_ANALYTICS}${allowParamsRegex}`}
      />
      {/* Legacy Analytics Page */}
      <Route
        component={PropertyDetailsPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/analytics${allowParamsRegex}`}
      />
      {/* Metrics summary Page */}
      <Route
        component={PropertyDetailsPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/metrics${allowParamsRegex}`}
      />
      {/* TODO: put the path in pathsManifest (which I think JS infra is refactoring) */}
      {/* TODO: confirm path with Scott/Leon/Tricia. May want to prepend screening-criteria with lead management */}
      <Route exact={true} path={redirectRouteList.screeningCriteria.path}>
        {redirectRouteList.screeningCriteria.getRedirectRoute()}
      </Route>
      {/* redirect /user/edit-listing/:propertyId to /properties/:propertyId/listing */}
      <Route exact={true} path={redirectRouteList.editListing.path}>
        {redirectRouteList.editListing.getRedirectRoute()}
      </Route>
      {/* Legacy route v1: leads for listing item */}
      {/* redirect /rental-manager/leads/:propertyId to /rental-manager/properties/:propertyId/contacts */}
      <Route exact={true} path={redirectRouteList.leads.path}>
        {redirectRouteList.leads.getRedirectRoute()}
      </Route>
      {/* Legacy route v2: leads for listing item */}
      {/* redirect /rental-manager/properties/:propertyId/inbox to /rental-manager/properties/:propertyId/contacts */}
      <Route exact={true} path={redirectRouteList.propertyDetailsInbox.path}>
        {redirectRouteList.propertyDetailsInbox.getRedirectRoute()}
      </Route>
      {/* Legacy route v2: leads for listing item */}
      {/* redirect /rental-manager/properties/:propertyId/leads to /rental-manager/properties/:propertyId/inbox */}
      <Route exact={true} path={redirectRouteList.propertyLeadsPage.path}>
        {redirectRouteList.propertyLeadsPage.getRedirectRoute()}
      </Route>
      {/* lead item */}
      <Route component={LeadPage} exact={true} path={`${paths.leadPage}/:leadId`} />
      {/* Legacy route v1: statistics */}
      {/* redirect /rental-manager/statistics/:propertyId to /rental-manager/properties/:propertyId/statistics */}
      <Route exact={true} path={redirectRouteList.statistics.path}>
        {redirectRouteList.statistics.getRedirectRoute()}
      </Route>
      {/* Legacy route v2: statistics */}
      {/* redirect /rental-manager/properties/:propertyId/statistics to /rental-manager/properties/:propertyId/analytics */}
      <Route exact={true} path={redirectRouteList.propertyDetailsStatistics.path}>
        {redirectRouteList.propertyDetailsStatistics.getRedirectRoute()}
      </Route>
      {/* legacy route: listing teardown */}
      <Route exact={true} path={redirectRouteList.listingTeardownPage.path}>
        {redirectRouteList.listingTeardownPage.getRedirectRoute()}
      </Route>
      {/* Submit request */}
      <Route component={SubmitRequestPage} exact={true} path={`${paths.submitRequest}${allowParamsRegex}`} />
      {/* paid inclusion payment */}
      <Route
        component={MobilePaymentPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/payToActivate${allowParamsRegex}`}
      />
      {/* inquiry contact */}
      <Route component={InquiryContactPage} exact={true} path={`${paths.inquiryContactPage}`} />

      {/* route orders important, don't change}
      {/* rent guarantee coverage level selection page */}
      <Route
        component={RentGuaranteeCoverageLevelSelectionPage}
        exact={true}
        path={`${paths.rentGuaranteePage}/${paths.properties}/:propertyId/coverageSelection${allowParamsRegex}`}
      />
      {/* rent guarantee multi policy manage policies payment dashboard page - <moduleIdentifier> */}
      <Route
        component={RentalProtectionPremiumPaymentsPage}
        exact={true}
        path={`${paths.rentGuaranteePage}/premiumPayments/:policyId*${allowParamsRegex}`}
      />
      {/* rent guarantee multi policy manage policies page */}
      <Route
        component={RentGuaranteeManagePoliciesPage}
        exact={true}
        path={`${paths.rentGuaranteePage}/${paths.properties}/:propertyId/managePolicies${allowParamsRegex}`}
      />
      {/* rent guarantee multi policy manage policies page - <moduleIdentifier> */}
      <Route
        component={RentGuaranteeManagePoliciesPage}
        exact={true}
        path={`${paths.rentGuaranteePage}/${paths.properties}/:propertyId/managePolicies/:moduleId*${allowParamsRegex}`}
      />
      {/* rent guarantee */}
      <Route
        component={RentGuaranteePage}
        exact={true}
        path={`${paths.rentGuaranteePage}/${paths.properties}/:propertyId*${allowParamsRegex}`}
      />
      {/* touring */}
      <Route
        component={TourAvailabilityPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/tours/tour-availability${allowParamsRegex}`}
      />
      <Route
        component={TourFullCalendarPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/tours/tour-calendar${allowParamsRegex}`}
      />
      {/* zillow routes */}
      {/* https://stackoverflow.com/questions/43289132/react-router-v4-global-no-match-to-nested-route-childs/43301397#comment74979219_43831238 */}

      {/* on localhost: redirect / to properties page */}
      <Route
        exact={true}
        path={`${paths.base}([/])?${allowParamsRegex}`}
        render={() => <Redirect to={{ pathname: paths.propertiesPage }} />}
      />

      {/* redirect /post-a-listing to properties page and pop modal */}
      <Route exact={true} component={ListingLandingPage} path={`${paths.postAListingPage}([/])${allowParamsRegex}`} />
      {/* Legacy ZRM redirect: /create/listing to /properties // Need to use addQueryParams here, which doesn't work in legacyRouteRedirects */}
      <Route
        path={`${paths.legacyZRM.createListing}/(.*)?`}
        addQueryParams={{ postingPath: 'true' }}
        render={() => <Redirect to={{ pathname: paths.propertiesPage }} />}
      />
      <Route
        component={ListingAvailabilityConfirmationPage}
        exact={true}
        path={`${paths.propertiesPage}/:propertyId/listing-availability${allowParamsRegex}`}
      />
      {/* marketing routes */}
      {/* payments */}
      <Route
        component={PropertiesPageWrapper}
        exact={true}
        path={`${paths.marketingRoutes.onboarding.payments}${allowParamsRegex}`}
      />
      {/* applications */}
      <Route
        component={PropertiesPageWrapper}
        exact={true}
        path={`${paths.marketingRoutes.onboarding.applications}${allowParamsRegex}`}
      />
      {/* screening */}
      <Route
        ssrRedirect={paths.marketingRoutes.screeningLangingPage}
        redirectStatus={307}
        component={PropertiesPageWrapper}
        exact={true}
        path={`${paths.marketingRoutes.screening}${allowParamsRegex}`}
      />
      {/* collect-rent-online */}
      <Route
        ssrRedirect={paths.marketingRoutes.collectRentLandingPage}
        redirectStatus={307}
        component={PropertiesPageWrapper}
        exact={true}
        path={`${paths.marketingRoutes.collectRent}${allowParamsRegex}`}
      />
      {/* legacy ZRM route redirects */}
      {brand === zillowBrand && __SERVER__ && legacyRouteRedirects(paths)}
      <Route
        render={() => <ErrorPage type={ERROR_PAGE_TYPES.TECHNICAL_DIFFICULTIES} />}
        exact={true}
        path={`${paths.base}/technical-difficulties`}
      />

      {/** 404 * */}
      <Route path="*" exact={true} render={() => <ErrorPage type={ERROR_PAGE_TYPES.NOT_FOUND} />} />
    </Switch>
  );
}

export { FULL_SCREEN_WIZARD_VIEW_PATHS };
